import { Box, HStack, Input, Text } from "@chakra-ui/react";
import { CornerDownLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { iconSize } from "~/components/ui/icon-size";
import { Kbd } from "~/components/ui/kbd";
import { branding } from "~/config/branding";
import { LangyMark } from "~/features/langy/components/LangyMark";

const LANGY_INPUT_RADIUS = "var(--radius-3)";

/**
 * The focused "Ask Langy" state of the command bar.
 *
 * Drawn on the Ledger tokens like the rest of the palette: a 32px field with
 * Langy's own mark, the input border at rest and the accent border with the
 * focus ring while focused. No sheen, gradient or AI treatment: the mark is
 * monochrome and nothing decorative moves (DEVIATIONS.md, D-10, D-11).
 */
export function CommandBarLangyMode({
  query,
  onQueryChange,
  onSubmit,
  onExit,
  exiting,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  /** Enter — hand the question to Langy. */
  onSubmit: () => void;
  /** Escape / Backspace-on-empty — return to normal command mode. */
  onExit: () => void;
  /** The panel handoff has started; keep this field inert until it closes. */
  exiting: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // Take focus on entry and put the cursor after the query carried over from
  // normal command search.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (exiting) {
      event.preventDefault();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
      return;
    }
    // Escape stays inside the mode switch. It must not bubble to the provider's
    // global Escape handler, which would close the entire command bar.
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onExit();
      return;
    }
    if (event.key === "Backspace" && query.length === 0) {
      event.preventDefault();
      onExit();
    }
  };

  return (
    <Box
      className="langy-root"
      data-langy-command-mode="true"
      background="transparent"
    >
      <Box paddingX="var(--space-4)" paddingY="var(--space-3)">
        <Box position="relative" borderRadius={LANGY_INPUT_RADIUS}>
          <HStack
            gap="var(--space-3)"
            minHeight="calc(var(--control-lg) + var(--space-2))"
            paddingX="var(--space-3)"
            borderWidth="1px"
            borderStyle="solid"
            borderColor={
              focused ? "var(--color-accent)" : "var(--color-border-input)"
            }
            borderRadius={LANGY_INPUT_RADIUS}
            background="var(--color-surface)"
            boxShadow={focused ? "var(--color-focus-ring)" : undefined}
            transition="border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out)"
          >
            <Box
              flexShrink={0}
              display="grid"
              placeItems="center"
              width="var(--icon-lg)"
              aria-hidden
            >
              <LangyMark size={20} />
            </Box>

            <Input
              ref={inputRef}
              aria-label={`Ask ${branding.assistantName}`}
              aria-busy={exiting}
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={handleKeyDown}
              placeholder={`Ask ${branding.assistantName} about this project…`}
              readOnly={exiting}
              autoComplete="off"
              border="none"
              outline="none"
              boxShadow="none"
              background="transparent"
              fontSize="var(--text-14)"
              lineHeight="var(--leading-normal)"
              flex={1}
              minWidth={0}
              color="var(--color-fg)"
              _placeholder={{ color: "var(--color-fg-placeholder)" }}
              _focus={{
                boxShadow: "none",
                outline: "none",
                background: "transparent",
              }}
              _focusVisible={{
                boxShadow: "none",
                outline: "none",
              }}
            />

            <Text
              flexShrink={0}
              fontSize="var(--text-12)"
              fontWeight="medium"
              color="var(--color-fg-2)"
            >
              {branding.assistantName}
            </Text>
          </HStack>
        </Box>
      </Box>

      <HStack
        justify="space-between"
        gap="var(--space-4)"
        paddingX="var(--space-4)"
        paddingY="var(--space-2)"
        borderTopWidth="1px"
        borderColor="var(--color-border-2)"
        color="var(--color-fg-2)"
        fontSize="var(--text-12)"
        flexWrap="wrap"
      >
        <Text>Ask about the project in plain language</Text>
        <HStack gap="var(--space-3)" flexShrink={0}>
          <HStack gap="var(--space-1)">
            <Kbd>
              <CornerDownLeft style={iconSize.sm} aria-hidden />
              <span className="visually-hidden">Enter</span>
            </Kbd>
            <Text>Ask</Text>
          </HStack>
          <HStack gap="var(--space-1)">
            <Kbd>Esc</Kbd>
            <Text>Back</Text>
          </HStack>
        </HStack>
      </HStack>
    </Box>
  );
}
